import React from 'react'
import { Building2, Crown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useHotelTone } from '@/lib/hotel-palette'

interface HotelSectionHeaderProps {
  name: string | null | undefined
  /** Which hotel this is — colour-bands the heading to match that hotel's chips elsewhere. */
  propertyId?: number | null
  /** The group's lead wears the crown — the hotel whose settings price every row on the page. */
  isLead?: boolean
  className?: string
}

/**
 * A HOTEL AS THE HEADING OF A BLOCK OF ITS ROOMS.
 *
 * The counterpart to `HotelChip`, and the reason both exist: a chip is an annotation that must not
 * out-shout the row it sits beside, so it is capped and truncates. A heading is the only place the
 * hotel's name appears for the rooms beneath it — capping it there is how a picker came to read
 * "Modern Studios Close To Hyd…", which is not a name anyone can act on. So: full name, wrapping if
 * it must, never clipped.
 *
 * Saying it once per block is also what buys every room name the full width — the alternative,
 * badging all six of a hotel's rooms, is what pushed a dropdown panel past the dialog that
 * contained it.
 *
 * Renders nothing without a name, so a caller may mount it unconditionally.
 */
export default function HotelSectionHeader({
  name,
  propertyId,
  isLead = false,
  className,
}: HotelSectionHeaderProps) {
  const toneFor = useHotelTone()
  const banded = toneFor(propertyId, name)

  if (!name) return null

  const Icon = isLead ? Crown : Building2

  return (
    <span
      className={cn(
        'flex min-w-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest',
        // Same hotel, same colour as its chips — a heading and a badge for one hotel must not
        // disagree, or the band stops being a reliable way to tell hotels apart.
        banded ? banded.text : isLead ? 'text-primary' : 'text-muted-foreground',
        className,
      )}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="break-words">{name}</span>
    </span>
  )
}
